import { FC } from 'react';
import { formatDistanceStrict } from 'date-fns';
import { FormField } from '@/components/FormField';
import { EditableSelect, type SelectOption } from '@/components/ui/editable-select';
import { EditableCombobox, type ComboboxOption } from '@/components/ui/editable-combobox';
import { EditableInput } from '@/components/ui/editable-input';
import { EditableTextarea, type MarkdownMode } from '@/components/ui/editable-textarea';
import { EnhancedDatePicker } from '@/components/ui/enhanced-date-picker';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CharacterCount } from '@/components/ui/character-count';
import { cn } from '@/lib/utils';
import {
  PACKET_VALIDATION,
  PacketStatus,
  RoleType,
  Decision,
  Ratification,
  User,
  Invitation,
} from '@promo/shared';
import { DecisionBadge } from '@/components/ui/DecisionBadge';
import { CoManagersField } from './CoManagersField';

export interface PacketFieldsProps {
  /** Controlled values for packet fields */
  values: {
    seniorLeaderId: string;
    currentTitle: string;
    targetTitle: string;
    lastPromotedDate: Date | null;
    year: string;
    summary: string;
    managerId: string;
  };
  /** Display name of the manager (read-only) */
  managerName: string;
  coManagers?: User[];
  pendingCoManagerInvitations?: Invitation[];
  showCoManagers?: boolean;
  canInviteCoManagers?: boolean;
  isInvitingCoManagers?: boolean;
  coManagerInviteInput?: string;
  coManagerValidationError?: string;
  onCoManagerInviteInputChange?: (value: string) => void;
  onRemoveCoManager?: (userId: string) => void;
  onRemovePendingCoManagerInvitation?: (invitationId: string) => void;
  seniorLeaderOptions: SelectOption[];
  titleOptions: ComboboxOption[];
  assessmentYearOptions: SelectOption[];
  packetStatus: PacketStatus;
  currentUserRole: RoleType | '';
  decision: Decision | null;
  disabled?: boolean;
  readOnly?: boolean;
  isLoadingLeaders?: boolean;
  errorLeaders?: string;
  className?: string;
  /** Callback when a field value changes */
  onFieldChange: (
    field: Exclude<keyof PacketFieldsProps['values'], 'managerId'>,
    value: string | Date | null
  ) => void;
  packetId: string;
  ratification?: Ratification | null;
  canViewCompletedDetails?: boolean;
  /** Summary markdown mode control */
  summaryMarkdownMode?: MarkdownMode;
  /** Summary markdown mode change callback */
  onSummaryMarkdownModeChange?: (mode: MarkdownMode) => void;
  /** Whether the section is in focused/edit mode */
  isFocused?: boolean;
}

const calculateTimeInRole = (date: Date | null): string => {
  if (!date) return 'N/A';
  try {
    return formatDistanceStrict(date, new Date(), { addSuffix: false });
  } catch {
    return 'Error';
  }
};

export const PacketFields: FC<PacketFieldsProps> = ({
  values,
  managerName,
  coManagers = [],
  pendingCoManagerInvitations = [],
  showCoManagers = false,
  canInviteCoManagers = false,
  isInvitingCoManagers = false,
  coManagerInviteInput = '',
  coManagerValidationError,
  onCoManagerInviteInputChange,
  onRemoveCoManager,
  onRemovePendingCoManagerInvitation,
  seniorLeaderOptions,
  titleOptions,
  assessmentYearOptions,
  packetStatus,
  currentUserRole,
  decision,
  disabled = false,
  readOnly = false,
  isLoadingLeaders = false,
  errorLeaders,
  className,
  onFieldChange,
  canViewCompletedDetails,
  summaryMarkdownMode = 'edit',
  onSummaryMarkdownModeChange,
  isFocused = false,
}) => {
  const timeInRole = calculateTimeInRole(values.lastPromotedDate);
  const isFieldDisabled = disabled || readOnly || !isFocused;

  // Determine if badge should be shown
  const isPrivilegedRoleForBadge =
    currentUserRole === RoleType.MANAGER ||
    currentUserRole === RoleType.HR ||
    currentUserRole === RoleType.SENIOR_LEADER;
  const isPacketCompletedForBadge = packetStatus === PacketStatus.COMPLETED;
  const renderMajorityBadge =
    decision !== null &&
    (isPrivilegedRoleForBadge || isPacketCompletedForBadge || canViewCompletedDetails);

  const reviewStatuses: PacketStatus[] = [
    PacketStatus.MANAGER_SUMMARY,
    PacketStatus.SENIOR_LEADER_REVIEW,
    PacketStatus.HR_REVIEW,
  ];

  const isInReviewStatus = reviewStatuses.includes(packetStatus);

  const canUserViewSummaryInReview =
    currentUserRole === RoleType.MANAGER ||
    currentUserRole === RoleType.SENIOR_LEADER ||
    currentUserRole === RoleType.HR;

  // For COMPLETED, use canViewCompletedDetails if present
  const isSummaryVisible =
    (isInReviewStatus && canUserViewSummaryInReview) ||
    (isPacketCompletedForBadge &&
      (canViewCompletedDetails !== undefined ? canViewCompletedDetails : true));

  let summaryShouldBeEditableByRoleAndStatus = false;
  if (
    isInReviewStatus &&
    (currentUserRole === RoleType.MANAGER || currentUserRole === RoleType.SENIOR_LEADER)
  ) {
    summaryShouldBeEditableByRoleAndStatus = true;
  }

  const summaryTextareaReadOnly = !summaryShouldBeEditableByRoleAndStatus;
  const summaryTextareaDisabled = disabled;

  // Check if summary is empty and should show callout
  const isSummaryEmpty = !values.summary.trim();
  const shouldShowSummaryCallout = isSummaryVisible && isSummaryEmpty && !summaryTextareaReadOnly;

  return (
    <TooltipProvider>
      <section className={cn('grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4', className)}>
        <FormField label="Senior leader" htmlFor="seniorLeaderId">
          <EditableSelect
            key={`${values.seniorLeaderId}-${seniorLeaderOptions.length}`}
            id="seniorLeaderId"
            value={values.seniorLeaderId}
            onValueChange={(value: string) => onFieldChange('seniorLeaderId', value)}
            options={seniorLeaderOptions}
            placeholder={isLoadingLeaders ? 'Loading...' : 'Pick a leader'}
            disabled={isFieldDisabled || isLoadingLeaders}
            contentClassName="bg-background text-popover-foreground"
            itemClassName="text-base"
          />
          {errorLeaders && <p className="text-red-500 text-sm mt-1">{errorLeaders}</p>}
        </FormField>

        <FormField label="Manager" htmlFor="manager">
          <EditableInput id="manager" value={managerName} disabled readOnly />
        </FormField>

        {showCoManagers && (
          <FormField label="Co-managers" htmlFor="coManagers" className="md:col-start-2">
            <CoManagersField
              id="coManagers"
              coManagers={coManagers}
              pendingInvitations={pendingCoManagerInvitations}
              disabled={disabled}
              canInvite={canInviteCoManagers && isFocused && !disabled}
              canRemove={canInviteCoManagers && isFocused && !disabled}
              isInviting={isInvitingCoManagers}
              inviteInput={coManagerInviteInput}
              validationError={coManagerValidationError}
              onInviteInputChange={onCoManagerInviteInputChange}
              onRemoveCoManager={onRemoveCoManager}
              onRemovePendingInvitation={onRemovePendingCoManagerInvitation}
            />
          </FormField>
        )}

        <FormField label="Current title" htmlFor="currentTitle">
          <EditableCombobox
            id="currentTitle"
            value={values.currentTitle}
            onValueChange={(value: string) => onFieldChange('currentTitle', value)}
            options={titleOptions}
            placeholder="Pick a title"
            disabled={isFieldDisabled}
            contentClassName="bg-background text-popover-foreground"
            itemClassName="text-base"
          />
        </FormField>

        <FormField label="Target title" htmlFor="targetTitle">
          <EditableCombobox
            id="targetTitle"
            value={values.targetTitle}
            onValueChange={(value: string) => onFieldChange('targetTitle', value)}
            options={titleOptions}
            placeholder="Pick a title"
            disabled={isFieldDisabled}
            contentClassName="bg-background text-popover-foreground"
            itemClassName="text-base"
          />
        </FormField>

        <FormField label="Last promoted" htmlFor="lastPromotedDate">
          <div className="flex items-center min-h-[40px]">
            <EnhancedDatePicker
              id="lastPromotedDate"
              value={values.lastPromotedDate}
              onValueChange={(date) => onFieldChange('lastPromotedDate', date)}
              disabled={disabled}
              readOnly={readOnly || !isFocused}
              placeholder="M/d/yy"
              ariaLabel="Last promoted date"
            />
            {values.lastPromotedDate && (
              <span className="text-sm text-muted-foreground ml-3">{timeInRole} ago</span>
            )}
          </div>
        </FormField>

        <FormField label="Assessment year" htmlFor="assessmentYear">
          <EditableSelect
            id="assessmentYear"
            value={values.year}
            onValueChange={(value: string) => onFieldChange('year', value)}
            options={assessmentYearOptions}
            placeholder="Select year"
            disabled={isFieldDisabled}
            contentClassName="bg-background text-popover-foreground"
            itemClassName="text-base"
          />
        </FormField>
        {/* Majority Decision Badge START */}
        {renderMajorityBadge && (
          <div className="md:col-span-2 mt-4 mb-2 flex items-center space-x-2">
            <DecisionBadge decision={decision} className="text-base px-5 py-2" />
          </div>
        )}
        {/* Majority Decision Badge END */}

        {/* Summary Section */}
        {isSummaryVisible && (
          <div className="col-span-1 md:col-span-2">
            <div className="space-y-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <label
                    htmlFor="summary"
                    className="text-sm font-medium text-gray-500 relative inline-flex items-center cursor-pointer"
                  >
                    Summary
                    {shouldShowSummaryCallout && (
                      <span className="relative flex w-2 h-2 ml-1">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                        <span className="relative inline-flex rounded-full w-2 h-2 bg-secondary"></span>
                      </span>
                    )}
                  </label>
                </TooltipTrigger>
                {shouldShowSummaryCallout && (
                  <TooltipContent className="bg-background border ml-4" side="bottom" align="start">
                    <p>Summarize 3 - 5 key points supporting the decision</p>
                  </TooltipContent>
                )}
              </Tooltip>
              <div className="relative">
                {!summaryTextareaReadOnly && !isFocused && (
                  <div
                    className="absolute inset-0 z-10 cursor-pointer"
                    aria-label="Edit summary"
                    onClick={() => {
                      // This would need to be handled by the parent component
                      // For now, just render the content
                    }}
                  />
                )}
                <EditableTextarea
                  id="summary"
                  value={values.summary}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    onFieldChange('summary', e.target.value)
                  }
                  markdown={true}
                  showModeToggle={!summaryTextareaReadOnly && isFocused}
                  markdownMode={summaryMarkdownMode}
                  onModeChange={onSummaryMarkdownModeChange}
                  placeholder="Write 3 - 5 key points supporting the decision for senior leaders and HR to review"
                  disabled={summaryTextareaDisabled}
                  readOnly={summaryTextareaReadOnly || !isFocused}
                  maxLength={PACKET_VALIDATION.MAX_SUMMARY_LENGTH}
                  className="min-h-[100px]"
                  onClick={() => {
                    if (!summaryTextareaReadOnly && !isFocused) {
                      // Handle entering edit mode - would need parent component support
                    } else if (isFocused && !summaryTextareaReadOnly) {
                      // Already in edit mode, allow normal interaction
                    }
                  }}
                />
                {!summaryTextareaReadOnly && isFocused && (
                  <CharacterCount
                    current={values.summary.length}
                    max={PACKET_VALIDATION.MAX_SUMMARY_LENGTH}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </TooltipProvider>
  );
};
